import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Target, Users, Check } from 'lucide-react';
import { HighlightRing } from '@/components/shared/SharedTasksSection';
import { Switch } from '@/components/ui/switch';
import { useDesign } from '@/contexts/DesignContext';
import { StCard, StAddRow } from '@/components/studio/planner/parts';

function YearView({
  currentDate,
  annualGoals,
  showAddGoal,
  setShowAddGoal,
  newGoal,
  setNewGoal,
  addAnnualGoal,
  toggleAnnualGoalComplete,
  deleteAnnualGoal,
  canManageShared,
  handleEditGoal,
  setHoveredItem,
  shareInfo,
  highlightSharedId,
}) {
  const { isStudio } = useDesign();
  const year = currentDate.getFullYear();

  const quarters = [
    { key: 'Q1', label: 'Q1', months: 'Jan - Mar' },
    { key: 'Q2', label: 'Q2', months: 'Apr - Jun' },
    { key: 'Q3', label: 'Q3', months: 'Jul - Sep' },
    { key: 'Q4', label: 'Q4', months: 'Oct - Dec' },
  ];

  const goalsByQuarter = {};
  const unassigned = [];
  (annualGoals || []).forEach(g => {
    if (g.quarter && ['Q1','Q2','Q3','Q4'].includes(g.quarter)) {
      if (!goalsByQuarter[g.quarter]) goalsByQuarter[g.quarter] = [];
      goalsByQuarter[g.quarter].push(g);
    } else {
      unassigned.push(g);
    }
  });

  const totalGoals = (annualGoals || []).length;
  const completedGoals = (annualGoals || []).filter(g => g.completed).length;

  const handleGoalKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      addAnnualGoal();
    }
  };

  const goalDialogContent = (
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Annual Goal</DialogTitle>
          <DialogDescription>Set a goal for {year}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Goal</Label>
            <Input
              value={newGoal.title}
              onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
              onKeyDown={handleGoalKeyDown}
              placeholder="What do you want to achieve?"
              data-testid="new-goal-title"
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              value={newGoal.description || ''}
              onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
              onKeyDown={handleGoalKeyDown}
              placeholder="Add details..."
              className="min-h-[80px]"
            />
          </div>
          <div className="space-y-2">
            <Label>Quarter</Label>
            <Select value={newGoal.quarter || 'none'} onValueChange={(value) => setNewGoal({...newGoal, quarter: value === 'none' ? null : value})}>
              <SelectTrigger><SelectValue placeholder="Assign to quarter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No quarter</SelectItem>
                <SelectItem value="Q1">Q1 (Jan - Mar)</SelectItem>
                <SelectItem value="Q2">Q2 (Apr - Jun)</SelectItem>
                <SelectItem value="Q3">Q3 (Jul - Sep)</SelectItem>
                <SelectItem value="Q4">Q4 (Oct - Dec)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {shareInfo?.can_share && (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-white/10 px-3 py-2.5">
              <div>
                <Label className="text-sm font-medium">Share with other admins</Label>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                  One synced goal for you + {shareInfo.admin_count} other admin{shareInfo.admin_count > 1 ? 's' : ''} — they'll be notified
                </p>
              </div>
              <Switch
                checked={!!newGoal.share}
                onCheckedChange={(v) => setNewGoal({ ...newGoal, share: v })}
                data-testid="share-goal-switch"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setShowAddGoal(false)}>Cancel</Button>
          <Button onClick={addAnnualGoal} className="bg-indigo-500 hover:bg-indigo-600" data-testid="submit-goal-btn">Add Goal</Button>
        </DialogFooter>
      </DialogContent>
  );

  const renderGoalRow = (goal) => (
    <div
      key={goal.id}
      className={`relative flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
        goal.completed ? 'bg-elevated border-slate-200 dark:border-white/[0.08] opacity-60' : 'bg-elevated border-slate-200 dark:border-white/[0.08] hover:border-indigo-200 dark:hover:border-indigo-400/40 hover:shadow-md hover:-translate-y-px dark:hover:bg-[#3a3d41]'
      }`}
      onClick={() => handleEditGoal(goal)}
      onMouseEnter={() => setHoveredItem?.(
        goal.is_shared
          ? (canManageShared?.(goal) ? { type: 'shared_goal', id: goal.id, data: goal, deleteFn: () => deleteAnnualGoal(goal.id) } : null)
          : { type: 'goal', id: goal.id, data: goal }
      )}
      onMouseLeave={() => setHoveredItem?.(null)}
      data-testid={`goal-row-${goal.id}`}
    >
      {highlightSharedId === goal.id && <HighlightRing />}
      <Checkbox
        checked={goal.completed}
        onCheckedChange={() => toggleAnnualGoalComplete(goal.id, goal.completed)}
        onClick={(e) => e.stopPropagation()}
        className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
        data-testid={`goal-check-${goal.id}`}
      />
      <div className="flex-1 min-w-0">
        <span className={`text-sm ${goal.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
          {goal.title}
        </span>
        {goal.is_shared && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-300 font-medium" data-testid={`goal-shared-badge-${goal.id}`}>
              <Users className="w-2.5 h-2.5" /> Shared
            </span>
            {goal.completed && goal.completed_by_name && (
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400">✓ {goal.completed_by_name}</span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleEditGoal(goal); }}>
          <Edit className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); deleteAnnualGoal(goal.id); }}>
          <Trash2 className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hover:text-red-500" />
        </Button>
      </div>
    </div>
  );

  // ── Studio (106 §2, Year option B) ───────────────────────────────────────────
  // A year strip, then the four quarters as columns, then the unassigned bucket.
  // The page title row and the year nav are rendered by PlannerApp.
  if (isStudio) {
    const currentQuarter = `Q${Math.floor(new Date().getMonth() / 3) + 1}`;
    const isThisYear = new Date().getFullYear() === year;
    const stGoalRow = (goal) => (
      <div
        key={goal.id}
        className={`st-prow${goal.completed ? ' st-done' : ''}`}
        onClick={() => handleEditGoal(goal)}
        onMouseEnter={() => setHoveredItem?.(
          goal.is_shared
            ? (canManageShared?.(goal) ? { type: 'shared_goal', id: goal.id, data: goal, deleteFn: () => deleteAnnualGoal(goal.id) } : null)
            : { type: 'goal', id: goal.id, data: goal }
        )}
        onMouseLeave={() => setHoveredItem?.(null)}
        data-testid={`goal-row-${goal.id}`}
      >
        {highlightSharedId === goal.id && <HighlightRing className="rounded-lg" />}
        <button
          type="button"
          className={`st-cb${goal.completed ? ' st-on' : ''}`}
          aria-label={goal.completed ? 'Mark not done' : 'Mark done'}
          onClick={(e) => { e.stopPropagation(); toggleAnnualGoalComplete(goal.id, goal.completed); }}
          data-testid={`goal-check-${goal.id}`}
        >
          {goal.completed ? <Check /> : null}
        </button>
        <span className="st-txt">
          <span>{goal.title}</span>
          {goal.is_shared && (
            <span className="st-chip" data-testid={`goal-shared-badge-${goal.id}`}>
              <Users /> Shared
            </span>
          )}
        </span>
        <span className="st-time">
          {goal.completed
            ? (goal.is_shared && goal.completed_by_name ? `✓ ${goal.completed_by_name}` : 'Done')
            : ''}
        </span>
        <span className="st-acts">
          <button type="button" className="st-ibtn" aria-label="Edit goal"
                  onClick={(e) => { e.stopPropagation(); handleEditGoal(goal); }}><Edit /></button>
          <button type="button" className="st-ibtn" aria-label="Delete goal"
                  onClick={(e) => { e.stopPropagation(); deleteAnnualGoal(goal.id); }}><Trash2 /></button>
        </span>
      </div>
    );
    const openAddGoal = (quarter) => { setNewGoal({ title: '', description: '', quarter, share: false }); setShowAddGoal(true); };
    return (
      <>
        <section className="st-card">
          <div className="st-ystrip">
            <b>{completedGoals} of {totalGoals} goal{totalGoals === 1 ? '' : 's'} done</b>
            <div className="st-seg4">
              {quarters.map((q) => {
                const goals = goalsByQuarter[q.key] || [];
                const done = goals.filter(g => g.completed).length;
                const pct = goals.length ? Math.round((done / goals.length) * 100) : 0;
                return <i key={q.key} className={isThisYear && q.key === currentQuarter ? 'st-now' : undefined} style={{ '--st-f': `${pct}%` }} />;
              })}
            </div>
            {isThisYear ? <span className="st-chip">{currentQuarter} in progress</span> : <span className="st-chip">{year}</span>}
            <div className="st-lbls">
              {quarters.map((q) => {
                const goals = goalsByQuarter[q.key] || [];
                return <span key={q.key}>{q.label} · {goals.filter(g => g.completed).length} of {goals.length}</span>;
              })}
            </div>
          </div>
        </section>

        <div className="st-qcols">
          {quarters.map((q) => {
            const goals = goalsByQuarter[q.key] || [];
            const done = goals.filter(g => g.completed).length;
            const pct = goals.length ? Math.round((done / goals.length) * 100) : 0;
            return (
              <StCard key={q.key} title={q.label} small={q.months} testId={`quarter-${q.key}`}>
                <div className="st-progrow">
                  <div className={`st-prog${goals.length && done === goals.length ? ' st-ok' : ''}`}><i style={{ width: `${pct}%` }} /></div>
                  <span>{done} of {goals.length}</span>
                </div>
                <div>{goals.map(stGoalRow)}</div>
                <StAddRow label="Add a goal" onClick={() => openAddGoal(q.key)} testId={`add-goal-${q.key}`} />
              </StCard>
            );
          })}
        </div>

        {unassigned.length > 0 && (
          <StCard
            title="Unassigned"
            small={`${unassigned.filter(g => g.completed).length} of ${unassigned.length}`}
            right={<span className="st-num">No quarter set</span>}
            testId="quarter-unassigned"
          >
            <div>{unassigned.map(stGoalRow)}</div>
          </StCard>
        )}

        <Dialog open={showAddGoal} onOpenChange={setShowAddGoal}>{goalDialogContent}</Dialog>
      </>
    );
  }

  return (
    <div>
      {/* Overall Progress */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{year} Goals</h2>
          {totalGoals > 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {completedGoals} of {totalGoals} complete
            </p>
          )}
        </div>
        <Dialog open={showAddGoal} onOpenChange={setShowAddGoal}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600" onClick={() => setNewGoal({ title: '', description: '', quarter: null, share: false })} data-testid="add-goal-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Goal
            </Button>
          </DialogTrigger>
          {goalDialogContent}
        </Dialog>
      </div>

      {/* Quarter Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quarters.map(q => {
          const goals = goalsByQuarter[q.key] || [];
          const done = goals.filter(g => g.completed).length;
          return (
            <div key={q.key} className="border border-slate-200 dark:border-white/10 rounded-xl p-4" data-testid={`quarter-${q.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{q.label}</span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">{q.months}</span>
                </div>
                {goals.length > 0 && (
                  <span className="text-xs text-slate-400 dark:text-slate-500">{done}/{goals.length}</span>
                )}
              </div>
              <div className="space-y-2">
                {goals.map(renderGoalRow)}
                <button
                  onClick={() => { setNewGoal({ title: '', description: '', quarter: q.key }); setShowAddGoal(true); }}
                  className="w-full py-3 flex items-center justify-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 hover:text-indigo-500 rounded-lg border border-dashed border-transparent hover:border-indigo-200 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/15 transition-all"
                  data-testid={`add-goal-${q.key}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add a goal
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Unassigned Goals */}
      {unassigned.length > 0 && (
        <div className="mt-4 border border-slate-200 dark:border-white/10 rounded-xl p-4" data-testid="quarter-unassigned">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-slate-800 dark:text-slate-100">Unassigned</span>
            <span className="text-xs text-slate-400 dark:text-slate-500">{unassigned.filter(g => g.completed).length}/{unassigned.length}</span>
          </div>
          <div className="space-y-2">
            {unassigned.map(renderGoalRow)}
          </div>
        </div>
      )}

      {/* Empty state */}
      {totalGoals === 0 && (
        <div className="text-center py-8">
          <Target className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">No goals yet</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">Set your first goal for {year}</p>
        </div>
      )}
    </div>
  );
}

export default YearView;
